// Swaps the browser's native confirm() for Simplero's own in-page modal on
// any [data-confirm] click (Rails UJS's declarative confirmation attribute),
// falling back to native confirm() if the modal isn't in this layout.
//
// Capture phase, and stopPropagation (not just preventDefault): native
// confirm() is truly OS-blocking, so the existing code could get away with
// confirming before deciding whether to prevent/stop the event. A modal is
// async — it returns immediately — so this has to win the race against Rails
// UJS's own bubble-phase confirm handling deterministically, regardless of
// script load order between this file and jquery_ujs/@rails-ujs.
//
// Excludes .themed-content-root (legacy theme-rendered, per-tenant content)
// the same way app/javascript/common/behaviors/rails_ujs.js already excludes
// it from Rails.formSubmitSelector — that content has its own independent
// confirm handling in app/javascript/site/behaviors/remote/index.js.
//
// A blank data-confirm="" is Rails UJS's own signal for "nothing to confirm"
// (jquery_ujs/@rails-ujs both check the message's truthiness, not the
// attribute's presence, before asking) — matched here so a blank value falls
// through untouched instead of popping an empty modal.
//
// No document-level side effects at module scope —
// installConfirmModalBridge() must be called explicitly — so this stays
// importable under plain `node --test` for the pure predicate below.

import { replayClick } from './boot_guard.js'

export function shouldInterceptConfirm({ hasMessage, insideThemedContent }) {
  return hasMessage && !insideThemedContent
}

export function installConfirmModalBridge() {
  document.addEventListener(
    'click',
    function (e) {
      const target = e.target.closest?.('[data-confirm]')
      const message = target?.getAttribute('data-confirm')
      if (
        !shouldInterceptConfirm({
          hasMessage: Boolean(message),
          insideThemedContent: Boolean(target?.closest('.themed-content-root')),
        })
      ) {
        return
      }

      e.preventDefault()
      e.stopPropagation()

      const proceed = () => replayClick(target, message)

      if (document.getElementById('confirm-modal')) {
        window.dispatchEvent(
          new CustomEvent('confirm-modal:open', { detail: { message, callback: proceed } })
        )
      } else if (window.confirm(message)) {
        proceed()
      }
    },
    true
  )
}
